<template>
  <div class="c-calculator">
    <div class="u-push">
      <p class="c-detail c-detail--main">
        <span>Pris bolig:</span>
        <span>{{ residencePrice.toLocaleString('nb-NO') }},–</span>
      </p>

      <template v-if="hasCommunalPrice">
        <p class="c-detail c-detail--sub">
          <template v-if="settings.depositDescription">
            <InfoButton
              :modal="{
                title: 'Innskudd',
                description: settings.depositDescription,
              }"
            >
              Innskudd ({{ depositSize }}%):
            </InfoButton>
          </template>
          <template v-else>
            <span>Innskudd ({{ depositSize }}%):</span>
          </template>
          <span>{{ baseDeposit.toLocaleString('nb-NO') }},–</span>
        </p>
      </template>

      <template v-if="hasCommunalPrice">
        <p class="c-detail c-detail--sub">
          <template v-if="settings.communalLoanDescription">
            <InfoButton
              :modal="{
                title: 'Andel felleslån',
                description: settings.communalLoanDescription
              }"
            >
              Andel felleslån ({{ 100 - depositSize }}%):
            </InfoButton>
          </template>
          <template v-else>
            Andel felleslån ({{ 100 - depositSize }}%):
          </template>
          <span>{{ baseLoan.toLocaleString('nb-NO') }},–</span>
        </p>
      </template>


      <p
        v-if="hasOptions"
        class="c-detail"
      >
        <span>Tilvalg:</span>
        <span>{{ optionsCost.toLocaleString('nb-NO') }},–</span>
      </p>

      <p
        v-if="residence.costs > 0"
        class="c-detail"
      >
        <span>Omkostninger:</span>
        <span>{{ residence.costs.toLocaleString('nb-NO') }},–</span>
      </p>

      <template
        v-if="hasOptions || residence.costs >= 0"
      >
        <p class="c-calculator__result c-detail u-border-top u-border-bottom u-border-bottom--thick">
          <span>Totalt:</span>
          <span>{{ totalPrice.toLocaleString('nb-NO') }},–</span>
        </p>
      </template>
    </div>

    <div class="u-push">
      <h3>Finansiering</h3>

      <p>Hvor mye egenkapital har du?</p>

      <p class="c-detail">
        <span>Egenkapital:</span>
        <currency-input
          v-model="equity"
          :locale="'nb-NO'"
          :currency="{suffix: ',–'}"
          :precision="0"
          :valueAsInteger="true"
          :allowNegative="false"
          class="c-detail__number"
        />
      </p>
      <input 
        v-model.number="equity" 
        @input="updateMortgage"
        @update="registerEvent(
          'Kalkulator: endre egenandel',
          'Calculator',
          'calculator_equity_range_slider'
        )"
        :max="totalPrice" 
        type="range"
        min="0"
        step="1000"
      >

      <template v-if="hasCommunalPrice">
        <p v-if="baseDeposit + optionsCost + residence.costs > equity">
          Med <strong>{{ equity.toLocaleString('nb-NO') }}</strong> kroner i egenkapital vil det gjenstå <strong>{{ equityResult.toLocaleString('nb-NO') }}</strong> kroner av innskudd<span v-if="hasOptions">, tilvalg</span><span v-if="residence.costs !== 0"> og omkostninger</span>. Dette kan du få hjelp i banken for å finansiere, se videre for å estimere lånekostnader.
        </p>
        <p v-else-if="baseDeposit + optionsCost + residence.costs === equity">
          <!--Du kan løse inn hele innskuddet.-->
          Med <strong>{{ equity.toLocaleString('nb-NO') }}</strong> kroner i egenkapital vil du kunne løse inn hele innskuddet <span v-if="hasOptions || residence.costs !== 0">og betale for </span><span v-if="hasOptions">alle tilvalg og </span><span v-if="residence.costs !== 0">omkostninger</span>.
        </p>
        <p v-else>
          Med <strong>{{ equity.toLocaleString('nb-NO') }}</strong> kroner i egenkapital vil du kunne løse inn hele innskuddet <span v-if="hasOptions || residence.costs !== 0">og betale for </span><span v-if="hasOptions">alle tilvalg og </span><span v-if="residence.costs !== 0">omkostninger</span>. Du kan også løse inn <strong>{{ equityResult.toLocaleString('nb-NO') }}</strong> kroner av felleslånet slik at det vil gjenstå <strong>{{ remainingLoan.toLocaleString('nb-NO') }}</strong> kroner.
        </p>
      </template>
      <template v-else>
        <p>Du trenger <strong>{{ equityResult.toLocaleString('nb-NO') }}</strong> kroner i lån for denne boligen.</p>
      </template>

      <template
        v-if="hasCommunalPrice && hasMortgage && equity < baseDeposit"
      >
        <p
          class="c-detail"
        >
          <span>Lånebeløp banklån:</span>
          <span>{{ mortgage.toLocaleString() }},–</span>
        </p>
        <input
          v-model.number="mortgage"
          @update="registerEvent(
            'Kalkulator: endre lånebeløp',
            'Calculator',
            'calculator_mortgage_range_slider'
          )"
          :min="mortgageMin"
          :max="mortgageMax + 1000"
          type="range"
          step="1000"
        >
      </template>

      <template
        v-if="hasCommunalPrice && hasMortgage && mortgage > 0 && equity < baseDeposit || !hasCommunalPrice && hasMortgage && equity < baseDeposit"
      >
        <p class="c-detail">
          <span>Rentesats banklån:</span>
          <span>{{ mortgageInterestRate }} %</span>
        </p>
        <input
          v-model.number="mortgageInterestRate"
          @update="(
            'Kalkulator: endre rentesats',
            'Calculator',
            'calculator_interest_rate_range_slider'
          )"
          type="range"
          min="0.5"
          max="5"
          step="0.1"
        >
      </template>

      <template
        v-if="hasCommunalPrice && hasMortgage && mortgage > 0 && equity < baseDeposit || !hasCommunalPrice && hasMortgage && equity < baseDeposit"
      >
        <p class="c-detail">
          <span>Nedbetalingstid banklån:</span>
          <span>{{ mortgageYears }} år</span>
        </p>
        <input
          v-model.number="mortgageYears"
          @update="(
            'Kalkulator: endre nedbetalingsperiode',
            'Calculator',
            'calculator_mortgage_years_range_slider'
          )"
          type="range"
          min="1"
          max="30"
          step="1"
        >
      </template>


      <template v-if="hasCommunalPrice && hasMortgage && mortgage > 0 && equity < baseDeposit">
        <p>Hvis du låner <strong>{{ mortgage.toLocaleString('nb-NO') }}</strong> vil det gjenstå <strong>{{ remainingLoan.toLocaleString() }}</strong> kroner i felleslån.</p>
      </template>
    </div>


    <div class="u-push">
      <h3>Du betaler i måneden</h3>

      <nav class="c-toggles">
        <input
          id="toggleMortgage"
          v-if="hasMortgage && mortgage > 0"
          @input="registerEvent(
            'Kalkulator: vis/skjul månedskost banklån',
            'Calculator',
            'calculator_mortgage_check'
          )"
          v-model="showMortgageCost"
          type="checkbox"
        >
        <label
          v-if="hasMortgage && mortgage > 0"
          for="toggleMortgage"
        >
          <template v-if="showMortgageCost">
            Skjul banklån 
          </template>
          <template v-else>
            Vis utgifter banklån (første {{ mortgageYears }} år)
          </template>
        </label>

        <input
          id="toggleInstallment"
          v-if="hasCommunalPrice && installmentFreeYears > 0"
          @input="registerEvent(
            'Kalkulator: vis/skjul avdragskostnad felleslån',
            'Calculator',
            'calculator_loan_check'
          )"
          v-model="showLoanInstallmentCost"
          type="checkbox"
        >
        <label
          v-if="hasCommunalPrice && installmentFreeYears > 0"
          for="toggleInstallment"
        >
          <template v-if="showLoanInstallmentCost">
            Skjul avdrag felleslån 
          </template>
          <template v-else>
            Vis felleslån med avdrag (utgifter etter {{ installmentFreeYears }} år)
          </template>
        </label>
      </nav>

      <template v-if="showMortgageCost">
        <p class="c-detail">
          <template v-if="settings.individualLoanDescription">
            <InfoButton
              :modal="{
                title: 'Banklån',
                description: settings.individualLoanDescription
              }"
            >
              Renter og avdrag banklån:
            </InfoButton>
          </template>
          <template v-else>
            <span>Renter og avdrag banklån:</span>
          </template>
          <span>{{ monthlyMortgage.toLocaleString('nb-NO') }},–</span>
        </p>
      </template>

      <template
        v-if="showLoanInterestCost"
      >
        <p class="c-detail">
          <template v-if="settings.interestRateCommunalDescription">
            <InfoButton
              :modal="{
                title: 'Felleslån',
                description: settings.interestRateCommunalDescription,
              }"
            >
              Renter <span v-if="showLoanInstallmentCost"> og avdrag </span> felleslån:
            </InfoButton>
          </template>
          <template v-else>
            <span>Renter <template v-if="showLoanInstallmentCost"> og avdrag </template> felleslån:</span>
          </template>
          <span v-if="showLoanInterestCost && showLoanInstallmentCost === false">{{ monthlyInterest.toLocaleString('nb-NO') }},–</span>
          <span v-else>{{ monthlyLoan.toLocaleString('nb-NO') }},–</span>
        </p>
      </template>

      <p
        v-if="showFixedCost"
        class="c-detail"
      >
        <template
          v-if="settings.monthlyCostsDescription"
        >
          <InfoButton
            :modal="{
              title: 'Fellesutgifter',
              description: settings.monthlyCostsDescription,
            }"
          >
            Fellesutgifter
          </InfoButton>
        </template>
        <template
          v-else
        >
          <span class="c-infobox__title">Fellesutgifter</span>
        </template>
        <span>{{ fixedMonthlyCost.toLocaleString('nb-NO') }},–</span>
      </p>

      <p class="c-calculator__result c-detail u-border-top u-border-bottom u-border-bottom--thick">
        <strong>Til sammen:</strong>
        <strong>{{ totalMonthly.toLocaleString('nb-NO') }},–</strong>
      </p>

      <template
        v-if="settings.taxDeductionCheck"
      >
        <p class="c-detail">
          <template
            v-if="settings.taxDeductionDescription"
          >
            <InfoButton
              :modal="{
                title: 'Skattefradrag',
                description: settings.taxDeductionDescription,
              }"
            >
              Etter skattefradrag:
            </InfoButton>
          </template>
          <template v-else>
            <span>Etter skattefradrag:</span>
          </template>
          <span>{{ totalMonthlyAfterTaxReduction.toLocaleString('nb-NO') }},–</span>
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import CalculatorSettings from '@/models/CalculatorSettings';
import Option from '@/models/Option';
import Residence from '@/models/Residence';

import { CurrencyInput, } from 'vue-currency-input';

import InfoButton from '@/components/InfoButton.vue';

export default {
  name: 'Calculator',
  components: {
    CurrencyInput,
    InfoButton,
  },
  props: {
    residenceId: {
      type: [
        Number,
        String,
        Boolean,
      ],
      required: true,
      default: false,
    },
  },
  data() {
    return {
      payment: 0,
      mortgage: 0,
      showMortgageCost : false,
      showLoanInterestCost : false,
      showLoanInstallmentCost : false,
      showFixedCost : false,
    };
  },
  computed: {
    hasCommunalPrice() {
      return this.$store.state.entities.calculatorsettings.hasCommunalPrice;
    },
    hasMortgage() {
      return this.$store.state.entities.calculatorsettings.hasMortgage;
    },
    hasOptions() {
      return this.$store.state.entities.calculatorsettings.hasOptions;
    },
    settings() {
      return CalculatorSettings.query().first();
    },
    residence() {
      return Residence
        .query()
        .whereId(this.residenceId)
        .with('levels')
        .first();
    },
    interestRateCommunal() {
      const interestRateCommunalExtras = this.settings.interestRateCommunalExtras;
      const residenceLevels = this.residence.levels;
      if (this.residence.hasInterestRate) {
        return this.residence.interestRate;
      } else if (interestRateCommunalExtras.length > 0 && residenceLevels.length > 0) {
        let interestRate = 0;
        interestRateCommunalExtras.forEach(extra => {
          residenceLevels.forEach(level => {
            if (extra.levelId === level.id) {
              interestRate = extra.interestRate;
            }
          });
        });
        if (interestRate !== 0) {
          return interestRate;
        }
      }
      return this.settings.interestRateCommunal;
    },
    communalLoanYears() {
      if (this.settings.communalLoanYears !== false) {
        return this.settings.communalLoanYears;
      }
      return 30;
    },
    depositSize() {
      if (this.residence.hasCustomDepositSize) {
        return this.residence.customDepositSize;
      }
      return this.settings.depositSize;
    },
    installmentFreeYears() {
      const installmentPeriods = this.settings.installmentPeriods;
      const levels = this.residence.levels;
      if (this.residence.hasInstallmentFreePeriod) {
        return this.residence.installmentFreePeriod;
      } else if (installmentPeriods.length > 0 && levels.length > 0) {
        let installmentFreePeriod = 0;
        this.settings.installmentPeriods.forEach(period => {
          this.residence.levels.forEach(level => {
            if (period.levelId === level.id) {
              installmentFreePeriod = period.years;
            }
          });
        });
        if (installmentFreePeriod !== 0) {
          return installmentFreePeriod;
        }
      }
      return false;
    },
    residencePrice() {
      return this.residence.price;
    },
    equity: {
      get() {
        return this.$store.state.entities.calculatorsettings.equity;
      },
      set(newValue) {
        // Store value in state and in user's localStorage to look up on repeat use.
        CalculatorSettings.commit(state => {
          state.equity = newValue;
        });
        localStorage.setItem('equity', newValue);
      },
    },
    mortgageInterestRate: {
      get() {
        return this.$store.state.entities.calculatorsettings.mortgageInterestRate;
      },
      set(newValue) {
        // Store value in state and in user's localStorage to look up on repeat use.
        CalculatorSettings.commit(state => {
          state.mortgageInterestRate = newValue;
        });
        localStorage.setItem('mortgageInterestRate', newValue);
      },
    },
    mortgageYears: {
      get() {
        return this.$store.state.entities.calculatorsettings.mortgageYears;
      },
      set(newValue) {
        // Store value in state and in users localStorage to look up on repeat use.
        CalculatorSettings.commit(state => {
          state.mortgageYears = newValue;
        });
        localStorage.setItem('mortgageYears', newValue);
      },
    },
    baseDeposit() {
      // Check if the residence has communal pricing,
      // if so return set portion of price as baseLoan,
      // otherwise return false
      if (this.hasCommunalPrice) {
        return this.residencePrice * (this.depositSize / 100);
      }
      return false;
    },
    baseLoan() {
      // Check if the residence has communal pricing,
      // if so return set portion of price as baseLoan,
      // otherwise return false
      if (this.hasCommunalPrice) {
        return this.residencePrice * ((100 - this.depositSize) / 100);
      }
      return false;
    },
    optionsCost() {
      if (this.hasOptions) {
        const options = Option.query()
          .where('isSelected', true)
          .get();
        let sum = 0;
        options.forEach(option => {
          sum += option.currentPrice;
        });
        return sum;
      }
      return false;
    },
    totalPrice() {
      return this.residencePrice + this.optionsCost + this.residence.costs;
    },
    equityResult() {
      if (this.hasCommunalPrice) {
        if (this.baseDeposit > this.equity) {
          return this.baseDeposit - this.equity;
        } else if (this.baseDeposit === this.equity) {
          return 0;
        } else {
          return this.equity - this.baseDeposit;
        }
      }
      return this.totalPrice - this.equity;
    },
    mortgageMin() {
      if (this.hasCommunalPrice) {
        if (this.baseDeposit > this.equity) {
          return this.baseDeposit - this.equity;
        } else {
          return 0;
        }
      }
      return 0;
    },
    mortgageMax() {
      return this.totalPrice - this.equity;
    },
    mortgageValue() {
      if (this.hasCommunalPrice) {
        return this.mortgage;
      }
      return this.totalPrice - this.equity;
    },
    remainingLoan() {
      if (this.hasCommunalPrice) {
        if (this.baseDeposit > this.equity) {
          return this.totalPrice - this.mortgage - this.equity;
        } else {
          if (this.mortgage + this.equityResult > this.baseLoan + this.residence.costs) {
            return 0;
          }
          return this.baseLoan + this.residence.costs - this.mortgage - this.equityResult;
        }
      }
      return this.totalPrice - this.equity;
    },
    monthlyMortgage() {
      if (this.hasMortgage) {
        return this.findMonthlyPayment(this.mortgageValue, this.mortgageInterestRate / 100, this.mortgageYears);
      }
      return false;
    },
    monthlyInterest() {
      if (this.hasCommunalPrice && this.remainingLoan > 0) {
        return Math.round(
          (this.remainingLoan / 12) * (this.interestRateCommunal / 100) +
            this.settings.interestFee,
        );
      }
      return 0;
    },
    monthlyLoan() {
      if (this.hasCommunalPrice && this.remainingLoan > 0) {
        return this.findMonthlyPayment(this.remainingLoan, this.interestRateCommunal / 100, this.communalLoanYears);
      }
      return 0;
    },
    fixedMonthlyCost() {
      return this.residence.size * this.settings.flexibleMonthlyCosts + this.settings.fixedMonthlyCosts;
    },
    totalMonthly() {
      let total = 0;
      if (this.showMortgageCost) {
        total += this.monthlyMortgage;
      }
      if (this.showLoanInterestCost && this.showLoanInstallmentCost === false) {
        total += this.monthlyInterest;
      }
      if (this.showLoanInterestCost && this.showLoanInstallmentCost) {
        total += this.monthlyLoan;
      }
      if (this.showFixedCost) {
        total += this.fixedMonthlyCost;
      }
      return total;
    },
    taxReduction() {
      if (this.settings.taxDeductionCheck) {
        const mortgageReduction = this.findMonthlyTaxReduction(
          this.mortgageValue,
          this.mortgageInterestRate / 100,
          this.settings.taxDeductionValue / 100
        );
        const loanReduction = this.findMonthlyTaxReduction(
          this.remainingLoan,
          this.interestRateCommunal / 100,
          this.settings.taxDeductionValue / 100,
        );
        return Math.round(mortgageReduction + loanReduction);
      }
      return false;
    },
    totalMonthlyAfterTaxReduction() {
      if (this.settings.taxDeductionCheck) {
        return this.totalMonthly - this.taxReduction;
      }
      return false;
    },
  },
  watch: {
    monthlyCost() {
    },
    mortgage() {
      this.checkMortageCost();
    },
  },
  mounted() {
    this.updateMortgage();
    this.checkMortageCost();
    this.checkLoanInterestCost();
    this.checkLoanInstallmentCost();
    this.checkFixedCost();
  },
  methods: {
    findMonthlyPayment(loan, interest, years) {
      /* =======================================================
       * Utregning månedsbeløp annuitetslån
       *
       * Formel:
       * y = G x ( r / ( 1 - (1+r)^-n ) )
       * Kilde til formel: https://skolediskusjon.no/kompendier/matematikk/regelbok-matte/annuitet/annuitetslaan
       *
       * y = månedbeløp
       * G = grunnbeløp (lånebeløp)
       * r = rentefot / månedlig rente
       * n = antall terminer
       * ====================================================== */
      const r = Math.round( ( (1 + interest) ** (1 / 12) - 1) * 100000 ) / 100000;
      const n = - (years * 12);
      const division = 1 - ((1 + r) ** n);
      const fraction = r / division;

      return Math.round(loan * fraction);
    },
    findMonthlyTaxReduction(loan, interestRate, deductionRate) {
      return loan * interestRate / 12 * deductionRate;
    },
    updateMonthlyCost() {
      Residence.update({
        id: this.residenceId,
        monthlyCost: this.monthlyCost,
      });
    },
    updateMortgage() {
      this.mortgage = this.mortgageMin;
    },
    // Register event in Google Analytics
    registerEvent(action, category, label) {
      if (window.__selector_settings__.trackers.gaCheck === true) {
        this.$gtag.event(action, {
          event_category: category,
          event_label: label,
        });
      }
    },
    updateShowMortgageCost(value) {
      this.showMortgageCost = value;
    },
    updateShowLoanInterestCost(value) {
      this.showLoanInterestCost = value;
    },
    updateShowLoanInstallmentCost(value) {
      this.showLoanInstallmentCost = value;
    },
    updateShowFixedCost(value) {
      this.showFixedCost = value;
    },
    toggleShowMortgageCost() {
      this.showMortgageCost = !this.showMortgageCost;
    },
    toggleShowLoanInterestCost() {
      this.showLoanInterestCost = !this.showLoanInterestCost;
    },
    toggleShowLoanInstallmentCost() {
      this.showLoanInstallmentCost = !this.showLoanInstallmentCost;
    },
    toggleShowFixedCost() {
      this.showFixedCost = !this.showFixedCost;
    },
    checkMortageCost() {
      if (this.hasCommunalPrice && this.hasMortgage && this.mortgage > 0) {
        this.updateShowMortgageCost(true);
      } else if (this.hasCommunalPrice === false && this.hasMortgage) {
        this.updateShowMortgageCost(true);
      } else {
        this.updateShowMortgageCost(false);
      }
    },
    checkLoanInterestCost() {
      if (this.hasCommunalPrice) {
        this.updateShowLoanInterestCost(true);
      } else {
        this.updateShowLoanInterestCost(false);
      }
    },
    checkLoanInstallmentCost() {
      if (this.hasCommunalPrice && this.installmentFreeYears === false) {
        this.updateShowLoanInstallmentCost(true);
      } else {
        this.updateShowLoanInstallmentCost(false);
      }
    },
    checkFixedCost() {
      if (this.hasCommunalPrice && this.fixedMonthlyCost > 0) {
        this.updateShowFixedCost(true);
      } else {
        this.updateShowFixedCost(false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.u-push {
  margin-bottom: 2em;
}

</style>